import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Crosshair, Link2, ArrowRight, AlertTriangle, Mail } from "lucide-react";
import ResultView from "../components/ResultView";
import EmailModal from "../components/EmailModal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Report() {
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [sectorLabel, setSectorLabel] = useState("");
  const [state, setState] = useState("loading");
  const [showEmail, setShowEmail] = useState(false);

  useEffect(() => {
    let live = true;
    (async () => {
      try {
        const [a, s] = await Promise.all([
          axios.get(`${API}/analyses/${id}`),
          axios.get(`${API}/sectors`).catch(() => ({ data: [] })),
        ]);
        if (!live) return;
        setData(a.data);
        const found = (s.data || []).find((x) => x.key === (a.data.sector || "general"));
        setSectorLabel(found ? found.label : "");
        setState("ok");
      } catch (e) { if (live) setState("notfound"); }
    })();
    return () => { live = false; };
  }, [id]);

  const copyLink = async () => {
    try { await navigator.clipboard.writeText(window.location.href); toast.success("Share link copied to clipboard"); }
    catch (e) { toast.error("Could not copy — please copy the URL from the address bar"); }
  };

  return (
    <div className="fl-app">
      <div className="fl-top">
        <div className="fl-wrap">
          <div className="fl-brandrow">
            <Link to="/" className="fl-brand" style={{ textDecoration: "none" }}>
              <Crosshair size={28} color="#c9a24b" />
              <div>
                <div className="fl-mark">LEX<span>GAP</span></div>
                <div className="fl-domain">lexgap.app</div>
              </div>
            </Link>
            <div className="fl-nav">
              <button className="fl-btn fl-btn--ghost fl-btn--sm" data-testid="report-copy-link-btn" onClick={copyLink}><Link2 size={15} /> Copy link</button>
              {state === "ok" && <button className="fl-btn fl-btn--ghost fl-btn--sm" data-testid="report-email-btn" onClick={() => setShowEmail(true)}><Mail size={15} /> Email</button>}
              <Link to="/" className="fl-btn fl-btn--primary fl-btn--sm" data-testid="report-run-own-btn">Run your own audit <ArrowRight size={15} /></Link>
            </div>
          </div>
          <p className="fl-tag"><b>Shared audit report</b> — a read-only forensic analysis. Anyone with this link can view it.</p>
        </div>
      </div>

      <div className="fl-wrap">
        {state === "loading" && (
          <div className="fl-card fl-loading" data-testid="report-loading"><div className="fl-spin" /><div style={{ color: "#fbf7ec", fontWeight: 600 }}>Loading shared report…</div></div>
        )}
        {state === "notfound" && (
          <div className="fl-empty" data-testid="report-notfound">
            <div className="ic"><AlertTriangle size={30} /></div>
            <h3>Report not found</h3>
            <p>This audit link is invalid or the analysis no longer exists.</p>
            <Link to="/" className="fl-btn fl-btn--primary" style={{ marginTop: 16 }}>Go to LexGap</Link>
          </div>
        )}
        {state === "ok" && data && (
          <div style={{ marginTop: 22 }}>
            <div className="print-head">
              <div className="ph-brand">LexGap · lexgap.app</div>
              <div className="ph-title">{data.title || "Forensic Analysis Report"}</div>
              <div className="ph-date">{new Date(data.created_at).toLocaleString()}</div>
            </div>
            <ResultView result={data.result} sectorLabel={sectorLabel} analysisId={id} firm={(data.firm || "").trim()} />
          </div>
        )}
      </div>

      {showEmail && <EmailModal analysisId={id} onClose={() => setShowEmail(false)} />}
    </div>
  );
}
